import { GetStaticProps, NextPage } from "next";
import React from "react";
import { Header } from "components/front/layout/Header";
import HeroArea from "components/front/lp/animefurusato/HeroArea";
import styled from "styled-components";
import AnimeFurusatoLpHead from "components/front/head/AnimeFurusatoLpHead";
import AnchorTarget from "components/front/layout/AnchorTarget";
import { imagePath } from "components/front/lp/animefurusato/image/util";
import DescriptionArea from "components/front/lp/animefurusato/DescriptionArea";
import { Props as SystemAreaProps } from "components/front/lp/animefurusato/SystemArea";
import { Props as DialogueAreaProps } from "components/front/lp/animefurusato/DialogueArea";
import { Props as CalculationAreaProps } from "components/front/lp/animefurusato/CalculationArea";
import { Props as DeductionFlowAreaProps } from "components/front/lp/animefurusato/DeductionFlowArea";
import { Props as FaqAreaProps } from "components/front/lp/animefurusato/FaqArea";
import { Props as BackToTopProps } from "components/front/layout/BackToTop";
import { Props as FooterProps } from "components/front/layout/Footer";
import { Props as GeneralFootProps } from "components/front/layout/GeneralFoot";
import { ACTION_CONTENT_WIDTH } from "components/front/lp/animefurusato/calculation/theme";
import dynamic from "next/dynamic";
import { FrontAuthProps, withFrontAuth } from "lib/hoc/withFrontAuth";

const SystemArea = dynamic<SystemAreaProps>(() => import("components/front/lp/animefurusato/SystemArea"));
const DialogueArea = dynamic<DialogueAreaProps>(() => import("components/front/lp/animefurusato/DialogueArea"));
const CalculationArea = dynamic<CalculationAreaProps>(() => import("components/front/lp/animefurusato/CalculationArea"));
const DeductionFlowArea = dynamic<DeductionFlowAreaProps>(() => import("components/front/lp/animefurusato/DeductionFlowArea"));
const FaqArea = dynamic<FaqAreaProps>(() => import("components/front/lp/animefurusato/FaqArea"));
const BackToTop = dynamic<BackToTopProps>(() => import("components/front/layout/BackToTop"));
const Footer = dynamic<FooterProps>(() => import("components/front/layout/Footer"));
const GeneralFoot = dynamic<GeneralFootProps>(() => import("components/front/layout/GeneralFoot").then((m) => m.GeneralFoot));

const AnimeFurusatoLp: NextPage<FrontAuthProps> = () => {

  return (
    <>
      <AnimeFurusatoLpHead />
      <Header positionFixed={false} />
      <Root>
        <AnchorTarget id="page_top" />
        <StyledHeroArea />
        <AppealArea>
          <StyledDescriptionArea />
          <StyledSystemArea />
          <StyledDialogueArea />
        </AppealArea>
        <StyledCalculationArea />
        <StyledDeductionFlowArea />
        <StyledFaqArea />
        <BackToTop />
      </Root>
      <Footer />
      <GeneralFoot />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};

export default withFrontAuth(AnimeFurusatoLp);

const Root = styled.div`
  background-color: #101010;
  color: #fff;
  @media screen and (max-width: 960px) {
    font-size: 14px;
  }
  @media screen and (min-width: 961px) and (max-width: 1440px) {
    font-size: 16px;
  }
  @media screen and (min-width: 1441px) {
    font-size: 18px;
  }
`;
const StyledHeroArea = styled(HeroArea)`
  @media screen and (max-width: 960px) {
    height: 104vw;
  }
`;
const AppealArea = styled.div`
  background-repeat: no-repeat;
  @media screen and (max-width: 600px) {
    background-image: url(${imagePath("/appeal/bg-sp.png")});
    background-size: 100%;
  }
  @media screen and (min-width: 601px) {
    background-position: 50% 0;
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    background-image: url(${imagePath("/appeal/bg-2-sp.png")});
  }
  @media screen and (min-width: 961px) and (max-width: 1440px) {
    background-image: url(${imagePath("/appeal/bg-1-pc.png")});
  }
  @media screen and (min-width: 1441px) and (max-width: 1920px) {
    background-image: url(${imagePath("/appeal/bg-2-pc.png")});
  }
  @media screen and (min-width: 1921px) {
    background-image: url(${imagePath("/appeal/bg-3-pc.png")});
  }
`;
const StyledDescriptionArea = styled(DescriptionArea)`
  @media screen and (max-width: 600px) {
    height: 88vw;
    font-size: 3.5vw;
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    height: 520px;
    font-size: 21px;
  }
  @media screen and (min-width: 961px) and (max-width: 1440px) {
    height: 500px;
    font-size: 20px;
  }
  @media screen and (min-width: 1441px) and (max-width: 1920px) {
    height: 590px;
    font-size: 24px;
  }
  @media screen and (min-width: 1921px) {
    height: 800px;
    font-size: 30px;
  }
`;
const StyledSystemArea = styled(SystemArea)`
  @media screen and (max-width: 600px) {
    font-size: 2.7vw;
    height: 186vw;
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    font-size: 20px;
    height: 1130px;
  }
  @media screen and (min-width: 961px) and (max-width: 1440px) {
    height: 690px;
    font-size: 20px;
  }
  @media screen and (min-width: 1441px) and (max-width: 1920px) {
    height: 830px;
    font-size: 24px;
  }
  @media screen and (min-width: 1921px) {
    height: 940px;
    font-size: 24px;
  }
`;
const StyledDialogueArea = styled(DialogueArea)`
  @media screen and (max-width: 600px) {
    height: 170vw;
    font-size: 3vw;
  }
  @media screen and (min-width: 601px) and (max-width: 960px) {
    height: 1010px;
    width: 600px;
    font-size: 18px;
    margin: 0 auto;
  }
  @media screen and (min-width: 961px) and (max-width: 1440px) {
    height: 825px;
    font-size: 20px;
  }
  @media screen and (min-width: 1441px) and (max-width: 1920px) {
    height: 1010px;
    font-size: 20px;
  }
  @media screen and (min-width: 1921px) {
    height: 1340px;
    font-size: 22px;
  }
`;
const StyledCalculationArea = styled(CalculationArea)`
  ${ACTION_CONTENT_WIDTH};
  @media screen and (max-width: 960px) {
    margin: 0 20px 40px;
  }
  @media screen and (min-width: 961px) {
    margin: 0 auto 80px;
  }
`;
const StyledDeductionFlowArea = styled(DeductionFlowArea)`
  ${ACTION_CONTENT_WIDTH};
  @media screen and (max-width: 960px) {
    margin: 40px 10px;
  }
  @media screen and (min-width: 961px) {
    margin: 80px auto;
  }
`;
const StyledFaqArea = styled(FaqArea)`
  ${ACTION_CONTENT_WIDTH};
  @media screen and (max-width: 960px) {
    margin: 40px auto 0;
  }
  @media screen and (min-width: 961px) {
    margin: 80px auto 0;
  }
`;
